import { computed, makeObservable } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'
import type { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'

import subscriptionAddons from 'modules/billing/store/collections/subscriptionAddons'
import type SubscriptionAddon from 'modules/billing/store/models/SubscriptionAddon'
import forms from 'modules/surveys/store/collections/forms'
import Form from 'modules/surveys/store/models/Form'

import configurator from './SubscriptionAddonConfigurator'
import type { ConfigType } from './SubscriptionAddonConfigurator'

interface ActivateOptions {
  skipOnSuccess?: boolean
}
type DeactivateOptions = ActivateOptions

export interface ActivatePayload {
  reviewer_id?: string
  bookkeeper_id?: string
  bookkeeper_ids?: string[]
  payroll_policy_id?: string
  add_employees_to_policy?: boolean
  country?: string
  complaints_manager_ids?: string[]
  regular_access_delay_in_days?: number
  desky_organization_api_key?: string
  idealdisc_api_key?: string
  min_rating?: number
  max_rating?: number
  linkedin_id?: string
}

export default class SubscriptionAddonInstaller {
  subscriptionAddon: SubscriptionAddon
  navigation: Navigation<NavigationPaths>

  constructor(subscriptionAddon: SubscriptionAddon, navigation: Navigation<NavigationPaths>) {
    this.subscriptionAddon = subscriptionAddon
    this.navigation = navigation
    makeObservable(this, {
      dependeeId: computed,
      disabledDependee: computed,
      enabledDependers: computed,
    })
  }

  get config(): ConfigType {
    return configurator.getSubscriptionAddonConfig(this.subscriptionAddon, this.navigation)
  }

  get dependeeId(): string | null | undefined {
    return this.config.dependeeId
  }

  get disabledDependee(): SubscriptionAddon | null | undefined {
    if (this.dependeeId) {
      return subscriptionAddons.find({
        id: this.dependeeId,
        enabled: false,
      })
    }
  }

  get enabledDependers(): SubscriptionAddon[] {
    return subscriptionAddons.filter((addon) => {
      if (!addon.get('enabled')) return false

      const addonConfig = configurator.getSubscriptionAddonConfig(addon, this.navigation)

      return addonConfig.dependeeId === this.subscriptionAddon.attributes.id
    })
  }

  async activate(payload: ActivatePayload = {}, { skipOnSuccess = false }: ActivateOptions = {}) {
    if (this.disabledDependee) {
      const installer = new SubscriptionAddonInstaller(this.disabledDependee, this.navigation)
      await installer.activate(payload, {
        skipOnSuccess: true,
      })
    }

    const result = await asyncAction(() => this.subscriptionAddon.rpc('activate', payload))

    if (result.success) {
      const { onSuccessActivate, onSuccessActivateEnps } = this.config
      this.subscriptionAddon.set(result.value)

      if (this.config.id === Form.Types.enps) {
        const enpsApp = await forms.fetch({
          data: {
            type: Form.Types.enps,
          },
        })

        !skipOnSuccess &&
          enpsApp.length &&
          enpsApp[0]?.id &&
          onSuccessActivateEnps &&
          onSuccessActivateEnps(enpsApp[0].id)()
      }

      !skipOnSuccess && onSuccessActivate && onSuccessActivate()

      return Promise.resolve(result.value)
    }

    return Promise.reject(result.value.error)
  }

  async deactivate({ skipOnSuccess = false }: DeactivateOptions = {}) {
    if (this.enabledDependers.length > 0) {
      await Promise.all(
        this.enabledDependers.map((addon) => {
          const installer = new SubscriptionAddonInstaller(addon, this.navigation)

          return installer.deactivate({
            skipOnSuccess: true,
          })
        })
      )
    }

    const result = await asyncAction(() => this.subscriptionAddon.rpc('deactivate'))

    if (result.success) {
      this.subscriptionAddon.set(result.value)
      !skipOnSuccess && window.location.reload()

      return Promise.resolve(result.value)
    }

    return Promise.reject(result.value.error)
  }

  async toggle(payload?: ActivatePayload) {
    if (this.subscriptionAddon.attributes.enabled) {
      await this.deactivate()
    } else {
      await this.activate(payload)
    }
  }
}
